import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import useLocalStorage from '#/hooks/useLocalStorage';
import { getSubdomain } from '#/utils/helpers';
import PaxDisclaimerModal from '#/components/shared/PaxDisclaimerModal';
import EconomicoDisclaimerModal from '#/components/shared/EconomicoDisclaimerModal';

type PartnerContextType = {
  partnerKey: string | null;
  setPartnerKey: (key: string | null) => void;
  removePartnerKey: () => void;
  isPartnerMode: boolean;
};

const initialContext: PartnerContextType = {
  partnerKey: null,
  setPartnerKey: () => {},
  removePartnerKey: () => {},
  isPartnerMode: false,
};

export const PartnerContext = createContext<PartnerContextType>(initialContext);

type PartnerProviderProps = {
  children: ReactNode;
};

export function PartnerProvider({ children }: PartnerProviderProps) {
  const [partnerKey, setPartnerKeyStorage, removePartnerKeyStorage] =
    useLocalStorage('gg_partner_key', null);
  const [isPartnerMode, setIsPartnerMode] = useState<boolean>(!!partnerKey);

  const [showPaxDisclaimer, setShowPaxDisclaimer] = useState(false);
  const [showEconomicoDisclaimer, setShowEconomicoDisclaimer] = useState(false);
  const [paxDisclaimerAccepted, setPaxDisclaimerAccepted] = useLocalStorage(
    'pax_disclaimer_accepted',
    false
  );
  const [economicoDisclaimerAccepted, setEconomicoDisclaimerAccepted] =
    useLocalStorage('economico_disclaimer_accepted', false);

  useEffect(() => {
    const isPaxPartner = partnerKey === 'pax';
    const isEconomicoPartner = partnerKey === 'economico';
    setShowPaxDisclaimer(isPaxPartner && !paxDisclaimerAccepted);
    setShowEconomicoDisclaimer(
      isEconomicoPartner && !economicoDisclaimerAccepted
    );
  }, [partnerKey, paxDisclaimerAccepted, economicoDisclaimerAccepted]);

  const handleAcceptPaxDisclaimer = () => {
    setPaxDisclaimerAccepted(true);
    setShowPaxDisclaimer(false);
  };

  const handleClosePaxDisclaimer = () => {
    setShowPaxDisclaimer(false);
  };

  const handleAcceptEconomicoDisclaimer = () => {
    setEconomicoDisclaimerAccepted(true);
    setShowEconomicoDisclaimer(false);
  };

  const handleCloseEconomicoDisclaimer = () => {
    setShowEconomicoDisclaimer(false);
  };

  useEffect(() => {
    const subdomain = getSubdomain(window.location.href);
    if (subdomain !== null) {
      setPartnerKeyStorage(subdomain);
    }
  }, []);

  useEffect(() => {
    setIsPartnerMode(!!partnerKey);
  }, [partnerKey]);

  const setPartnerKey = (key: string | null) => {
    if (key) {
      setPartnerKeyStorage(key);
    } else {
      removePartnerKeyStorage();
    }
  };

  const removePartnerKey = () => {
    removePartnerKeyStorage();
  };

  const contextValue = useMemo(
    () => ({
      partnerKey,
      setPartnerKey,
      removePartnerKey,
      isPartnerMode,
    }),
    [partnerKey, isPartnerMode]
  );

  return (
    <PartnerContext.Provider value={contextValue}>
      {children}
      <PaxDisclaimerModal
        open={showPaxDisclaimer}
        onClose={handleClosePaxDisclaimer}
        onAccept={handleAcceptPaxDisclaimer}
      />
      <EconomicoDisclaimerModal
        open={showEconomicoDisclaimer}
        onClose={handleCloseEconomicoDisclaimer}
        onAccept={handleAcceptEconomicoDisclaimer}
      />
    </PartnerContext.Provider>
  );
}
